import PropTypes from 'prop-types'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
// Component
import PopupNotificationPage from '../../pages/PopupNotificationPage/PopupNotificationPage'
// Styled Component
import { NotiWrapper } from '../MainSidebar/MainSidebarStyled'
// Ant design
import { ExportOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons'
import {
  Alert,
  Avatar,
  Badge,
  Dropdown,
  Menu,
  Tabs,
  Tooltip,
  message,
} from 'antd'
// firebase
import firebase from 'firebase/app'
import 'firebase/messaging'
// Other
import { useKeycloak } from '@react-keycloak/web'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import { useSSO, vapidKey } from '../../config'
import { NOTIFICATION_STATUS } from '../../constants'
import utils from '../../utils'
import PopupAppGrid from '../PopupAppGrid/PopupAppGrid'
import SimpleBreadCrumbV2 from '../SimpleBreadCrumb/SimpleBreadCrumbV2'
import {
  HeaderAccountInfo,
  HeaderAccountName,
  HeaderAccountRole,
  HeaderAccountTrigger,
  HeaderActions,
  HeaderAlertWrapper,
  HeaderBarWrapper,
  HeaderBreadcrumbArea,
  HeaderGuideLink,
  HeaderNotificationTrigger,
  HeaderUserDropdownGlobalStyle,
  StyledUserMenu,
} from './MainHeaderBarStyled'
import { AltArrowDownOutlineIcon, BellLinearIcon } from '../AppIcon'

const { TabPane } = Tabs

const MainHeaderBar = props => {
  const messaging = useMemo(
    () => (firebase.messaging.isSupported() ? firebase.messaging() : null),
    []
  )

  const {
    authenticationStore,
    accountStore,
    history,
    companyStore,
    userStore,
    notificationStore,
    title,
  } = props

  const { unreadNotificationCount, unreadNewsCount } = notificationStore
  const {
    currentUserAvatar,
    currentUser,
    mustChangePassword,
  } = authenticationStore

  const [visibleNotification, setVisibleNotification] = useState(false)
  const [changedTabsNotification, setChangedTabsNotification] = useState({
    status: false,
    onlyNewsNotification: false,
    isOpen: false,
  })
  const [avatarSrc, setAvatarSrc] = useState(undefined)

  const disabledKeyCloak = {
    keycloak: null,
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { keycloak } = useSSO ? useKeycloak() : disabledKeyCloak
  const currentUserName = useMemo(
    () =>
      currentUser?.name_uppercase
        ? utils.getNameInCapitalize(currentUser.name_uppercase)
        : currentUser?.username || 'Admin',
    [currentUser]
  )
  const currentUserRole = useMemo(
    () => currentUser?.position?.name || 'Nhân viên',
    [currentUser]
  )
  const currentUserInitial = useMemo(
    () => (currentUserName ? currentUserName.split('')[0].toUpperCase() : 'A'),
    [currentUserName]
  )
  const guideHref = useMemo(
    () =>
      `${process.env.PUBLIC_URL}/guide/HDSD_v1.zip?date=${moment().format(
        'YYYY-MM-DD-HH-mm-ss'
      )}`,
    []
  )
  const changePasswordHref = useMemo(() => {
    const apiUrl = process.env.REACT_APP_API_URL || ''
    const baseUrl = apiUrl.includes('localhost')
      ? 'http://localhost:3000'
      : apiUrl
    return `${baseUrl}/my-profile/change-password`
  }, [])
  const unreadTotalCount = useMemo(
    () => unreadNewsCount + unreadNotificationCount,
    [unreadNewsCount, unreadNotificationCount]
  )

  useEffect(() => {
    if (!currentUserAvatar) {
      setAvatarSrc(undefined)
      return undefined
    }

    const objectUrl = URL.createObjectURL(currentUserAvatar)
    setAvatarSrc(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [currentUserAvatar])

  const clickLogout = useCallback(() => {
    accountStore.clearStore()
    companyStore.clearStore()
    userStore.clearStore()
    notificationStore.clearStore()
    authenticationStore.userLogout()

    if (keycloak) {
      keycloak.logout()
    }

    if (!useSSO) {
      history.replace('/')
    }

    if (messaging) {
      messaging
        .deleteToken()
        .then(res => console.log(res))
        .catch(err => console.log(err))
    }
  }, [
    accountStore,
    authenticationStore,
    companyStore,
    history,
    keycloak,
    messaging,
    notificationStore,
    userStore,
  ])

  useEffect(() => {
    if (messaging && !localStorage.getItem('FCMToken')) {
      messaging
        .getToken({ vapidKey: vapidKey })
        .then(currentToken => {
          if (currentToken) {
            localStorage.setItem('FCMToken', JSON.stringify(currentToken))
            notificationStore
              .sendTokenToSever(currentToken)
              .then(res => console.log(res))
              .catch(err => console.log(err.vi))
          } else {
            console.log(
              'No registration token available. Request permission to generate one.'
            )
          }
        })
        .catch(() => console.log('You have blocked notifications!'))
    }
    const unsubscribeOnMessage = messaging?.onMessage(async payload => {
      console.log('Message received. ', payload)
      if (payload.data.type === NOTIFICATION_STATUS.NEWS) {
        if (changedTabsNotification.isOpen) {
          setChangedTabsNotification({
            status: true,
            onlyNewsNotification: true,
            isOpen: true,
          })
        } else {
          await notificationStore.getUnreadNewsCount()
        }
      } else {
        if (changedTabsNotification.isOpen) {
          setChangedTabsNotification({
            status: true,
            onlyNewsNotification: false,
            isOpen: true,
          })
        } else {
          await notificationStore.getUnreadNotificationCount()
        }
      }
      notificationStore.setNotificationType(payload.data.type)
      message.info(`Thông báo mới: ${payload.notification.body}`)
    })
    return () => {
      if (typeof unsubscribeOnMessage === 'function') {
        unsubscribeOnMessage()
      }
    }
  }, [changedTabsNotification, messaging, notificationStore])

  useEffect(() => {
    if (utils.isIOSDevice()) {
      return undefined
    }

    const channel4Broadcast = new BroadcastChannel('channel4')
    channel4Broadcast.onmessage = async even => {
      if (even.data.type === NOTIFICATION_STATUS.NEWS) {
        if (changedTabsNotification.isOpen) {
          setChangedTabsNotification({
            status: true,
            onlyNewsNotification: true,
            isOpen: true,
          })
        } else {
          await notificationStore.getUnreadNewsCount()
        }
      } else {
        if (changedTabsNotification.isOpen) {
          setChangedTabsNotification({
            status: true,
            onlyNewsNotification: false,
            isOpen: true,
          })
        } else {
          await notificationStore.getUnreadNotificationCount()
        }
      }
    }

    return () => {
      channel4Broadcast.close()
    }
  }, [changedTabsNotification, notificationStore])

  const goToProfile = useCallback(() => {
    history.push('/my-profile')
  }, [history])

  const closeNotification = useCallback(() => {
    setVisibleNotification(false)
  }, [])

  const onVisibleNotificationChange = useCallback(flag => {
    setVisibleNotification(flag)
  }, [])

  const onChangeTabsNotification = useCallback(tab => {
    setChangedTabsNotification({
      status: true,
      onlyNewsNotification: tab === 'news',
      isOpen: true,
    })
  }, [])

  const goToNotificationList = useCallback(
    onlyNewsNotification => {
      setVisibleNotification(false)
      const queryStringParams = queryString.stringify({
        only_news_notification: onlyNewsNotification,
      })
      history.push(`/notification?${queryStringParams}`)
    },
    [history]
  )
  const goToAllNotifications = useCallback(() => {
    goToNotificationList(false)
  }, [goToNotificationList])
  const goToAllNews = useCallback(() => {
    goToNotificationList(true)
  }, [goToNotificationList])

  const onNotificationTriggerClick = useCallback(() => {
    if (!visibleNotification) {
      setChangedTabsNotification(current => ({
        status: true,
        onlyNewsNotification: current.onlyNewsNotification,
        isOpen: true,
      }))
    }
    setVisibleNotification(!visibleNotification)
    notificationStore.clearKeyword()
  }, [notificationStore, visibleNotification])

  const onNotificationTriggerKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onNotificationTriggerClick()
      }
    },
    [onNotificationTriggerClick]
  )

  const onAccountTriggerKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        closeNotification()
        event.currentTarget.click()
      }
    },
    [closeNotification]
  )

  const menu = useMemo(
    () => (
      <StyledUserMenu>
        <Menu.Item
          key="profile"
          className="menu-profile"
          style={{ borderRadius: 6 }}
          icon={<UserOutlined style={{ fontSize: 14, color: '#003a70' }} />}
          onClick={goToProfile}>
          Thông tin cá nhân
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          key="logout"
          className="menu-logout"
          style={{ borderRadius: 6 }}
          danger
          icon={<LogoutOutlined style={{ fontSize: 14 }} />}
          onClick={clickLogout}>
          Đăng xuất
        </Menu.Item>
      </StyledUserMenu>
    ),
    [clickLogout, goToProfile]
  )

  const notiWrapper = useMemo(
    () => (
      <NotiWrapper>
        <Tabs
          onChange={onChangeTabsNotification}
          defaultActiveKey="notification">
          <TabPane
            tab={
              <Badge
                count={
                  unreadNotificationCount > 99 ? '99+' : unreadNotificationCount
                }>
                <span style={{ paddingRight: 14, paddingLeft: 4 }}>
                  Thông báo
                </span>
              </Badge>
            }
            key="notification">
            <PopupNotificationPage
              changedTabsNotification={changedTabsNotification}
              onlyNewsNotification={false}
              visibleNotification={visibleNotification}
              setVisibleNotification={closeNotification}
            />
          </TabPane>
          <TabPane
            tab={
              <Badge count={unreadNewsCount > 99 ? '99+' : unreadNewsCount}>
                <span style={{ paddingRight: 14, paddingLeft: 4 }}>
                  Thông báo chung
                </span>
              </Badge>
            }
            key="news">
            <PopupNotificationPage
              changedTabsNotification={changedTabsNotification}
              onlyNewsNotification={true}
              visibleNotification={visibleNotification}
              setVisibleNotification={closeNotification}
            />
          </TabPane>
        </Tabs>
        <div className="noti-footer">
          <span
            role="button"
            tabIndex={0}
            onClick={
              changedTabsNotification.onlyNewsNotification
                ? goToAllNews
                : goToAllNotifications
            }
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (changedTabsNotification.onlyNewsNotification) {
                  goToAllNews()
                } else {
                  goToAllNotifications()
                }
              }
            }}>
            Xem thêm <ExportOutlined style={{ fontSize: 13 }} />
          </span>
        </div>
      </NotiWrapper>
    ),
    [
      changedTabsNotification,
      closeNotification,
      goToAllNews,
      goToAllNotifications,
      onChangeTabsNotification,
      unreadNewsCount,
      unreadNotificationCount,
      visibleNotification,
    ]
  )

  return (
    <HeaderBarWrapper>
      <HeaderUserDropdownGlobalStyle />
      <HeaderBreadcrumbArea>
        <SimpleBreadCrumbV2 title={title} />
      </HeaderBreadcrumbArea>

      <HeaderActions>
        {mustChangePassword && (
          <HeaderAlertWrapper>
            <Tooltip placement="top" title={'Mật khẩu của bạn đã quá hạn'}>
              <Alert
                message={
                  <span>
                    Mật khẩu của bạn đã quá hạn. Vui lòng đổi mật khẩu{' '}
                    <a href={changePasswordHref}>tại đây</a>
                  </span>
                }
                type="warning"
                showIcon
                closable
              />
            </Tooltip>
          </HeaderAlertWrapper>
        )}

        {/* <Tooltip placement="top" title={'Hướng dẫn sử dụng'}>
          <HeaderGuideLink href={guideHref}>
            Hướng dẫn sử dụng
          </HeaderGuideLink>
        </Tooltip> */}

        <PopupAppGrid />

        <Dropdown
          overlay={notiWrapper}
          placement="bottomRight"
          trigger={['click']}
          visible={visibleNotification}
          onVisibleChange={onVisibleNotificationChange}>
          <HeaderNotificationTrigger
            title="Thông báo"
            role="button"
            tabIndex={0}
            onClick={onNotificationTriggerClick}
            onKeyDown={onNotificationTriggerKeyDown}>
            <Badge
              count={unreadTotalCount > 99 ? '99+' : unreadTotalCount}
              offset={[-5, -2]}
              style={{ fontSize: '10px' }}>
              <div className="notificationWrapper">
                <BellLinearIcon className="header-notification-icon" />
              </div>
            </Badge>
          </HeaderNotificationTrigger>
        </Dropdown>

        <Dropdown
          overlay={menu}
          placement="bottomRight"
          trigger={['click']}
          overlayClassName="header-user-dropdown">
          <HeaderAccountTrigger
            title="Tài khoản"
            role="button"
            tabIndex={0}
            onClick={closeNotification}
            onKeyDown={onAccountTriggerKeyDown}>
            <Avatar className="header-avatar" size={48} src={avatarSrc}>
              {currentUserInitial}
            </Avatar>
            <HeaderAccountInfo>
              <HeaderAccountName>{currentUserName}</HeaderAccountName>
              <HeaderAccountRole>{currentUserRole}</HeaderAccountRole>
            </HeaderAccountInfo>
            <AltArrowDownOutlineIcon className="header-account-chevron" />
          </HeaderAccountTrigger>
        </Dropdown>
      </HeaderActions>
    </HeaderBarWrapper>
  )
}

MainHeaderBar.propTypes = {
  title: PropTypes.string,
}

export default memo(
  withRouter(
    inject(
      'authenticationStore',
      'accountStore',
      'userStore',
      'companyStore',
      'notificationStore',
      'loadingAnimationStore',
      'commonStore',
      'taskManagementStore',
      'taskManagementCommentStore'
    )(observer(MainHeaderBar))
  )
)
